// src/components/tour/company/DashboardTourSteps.tsx
import { Step } from 'react-joyride';

const texto = (children: React.ReactNode) => (
  <div style={{ textAlign: 'justify' }}>{children}</div>
);

const paso = (
  target: string,
  content: React.ReactNode,
  placement: Step['placement'] = 'top'
): Step => ({
  target,
  content: texto(content),
  placement,
  disableBeacon: true,
});

const DashboardTourSteps: Step[] = [
  paso(
    'body',
    <>
      Bienvenido al <strong>Dashboard</strong>. Aquí tienes una visión completa
      de tu negocio: finanzas, citas, bonos, la semana y el resumen del año.
    </>,
    'center'
  ),

  paso(
    '.tour-banner',
    <>
      Instala <strong>Bookoll como app</strong> en tu dispositivo para
      abrirlo más rápido y recibir avisos.
    </>,
    'bottom'
  ),

  paso(
    '.tour-month-selector',
    <>
      Elige el <strong>mes y el año</strong> que quieres consultar. Las
      métricas y el top de servicios se actualizan con el mes elegido.
    </>,
    'bottom'
  ),

  paso(
    '.tour-seccion-finanzas > button',
    <>
      El dashboard está dividido en <strong>secciones</strong>. Pulsa el
      título o la flecha <strong>⌄</strong> de la derecha para plegarlas o
      desplegarlas.
      <br />
      <br />
      Se recuerda cómo las dejaste: si vuelves, las verás igual.
    </>,
    'bottom'
  ),

  paso(
    '.tour-metric-facturadoMes',
    <>
      <strong>Dinero facturado en el mes</strong> por las citas completadas.
      El porcentaje lo compara con el mes anterior.
    </>
  ),

  paso(
    '.tour-metric-gastosMes',
    <>
      <strong>Gastos registrados en el mes</strong>. Aquí una subida sale en{' '}
      <span style={{ color: '#ef4444', fontWeight: 'bold' }}>rojo</span> y una
      bajada en{' '}
      <span style={{ color: '#22c55e', fontWeight: 'bold' }}>verde</span>.
    </>
  ),

  paso(
    '.tour-metric-beneficioMes',
    <>
      <strong>Beneficio neto del mes</strong>: ingresos menos gastos. La
      etiqueta indica el <em>margen</em> sobre los ingresos.
    </>
  ),

  paso(
    '.tour-metric-citasHoy',
    <>
      Cuántas <strong>citas tienes hoy</strong>.
      <br />
      <br />
      Las tarjetas con la flecha <strong>↗</strong> te llevan a su pantalla:
      esta abre el <strong>calendario</strong>.
    </>
  ),

  paso(
    '.tour-metric-citasMes',
    <>
      Total de <strong>citas del mes</strong>.{' '}
      <span style={{ color: '#22c55e', fontWeight: 'bold' }}>Verde</span> =
      mejora frente al mes anterior,{' '}
      <span style={{ color: '#ef4444', fontWeight: 'bold' }}>rojo</span> =
      empeora.
    </>
  ),

  paso(
    '.tour-metric-cancelacionesMes',
    <>
      <strong>Citas canceladas en el mes</strong>. Menos cancelaciones es
      mejor, por eso una bajada sale en verde.
    </>
  ),

  paso(
    '.tour-metric-trabajadores',
    <>
      <strong>Trabajadores activos</strong> que pueden recibir reservas.
    </>
  ),

  paso(
    '.tour-metric-serviciosDisponibles',
    <>
      <strong>Servicios activos</strong> que tus clientes pueden reservar
      online.
    </>
  ),

  paso(
    '.tour-metric-bonosDisponibles',
    <>
      <strong>Bonos activos</strong> que ofreces a tus clientes.
    </>
  ),

  paso(
    '.tour-metric-bonosVendidos',
    <>
      <strong>Bonos vendidos este mes</strong>.
    </>
  ),

  paso(
    '.tour-metric-usosBonos',
    <>
      <strong>Reservas pagadas con bono</strong> este mes: te dice si los
      bonos vendidos se están usando.
    </>
  ),

  paso(
    '.tour-metric-ingresosBonos',
    <>
      <strong>Ingresos por venta de bonos</strong> en el mes, se usen cuando
      se usen.
    </>
  ),

  paso(
    '.tour-chart-semana',
    <>
      <strong>Citas por día de la semana</strong>. Usa las flechas{' '}
      <strong>‹ ›</strong> para ir a otras semanas; pulsa el nombre de la
      semana para volver a la actual.
      <br />
      <br />
      La línea discontinua es la <strong>media de citas por día
      laborable</strong> de esa semana: los días cerrados (tachados) no
      cuentan.
    </>
  ),

  paso(
    '.tour-chart-servicios',
    <>
      Ranking de los <strong>servicios más reservados</strong> del mes. Cambia
      entre <strong>Citas</strong> e <strong>Ingresos</strong> para ordenarlos
      por una cosa u otra.
    </>
  ),

  paso(
    '.tour-horas-pico',
    <>
      <strong>¿Cuándo tienes más citas?</strong> Cada casilla es una hora de
      un día: cuanto más oscura, más tiempo ocupado con citas (una cita de 10:30 a 11:30 cuenta media hora en cada franja). La llama marca la hora punta y las
      casillas rayadas son horas en las que está cerrado.
      <br />
      <br />
      Pasa el ratón (o pulsa) sobre una casilla para ver el % de tiempo
      ocupado. Cambia a <strong>3 meses</strong> para tener más
      datos.
    </>
  ),

  paso(
    '.tour-horas-trabajadores',
    <>
      Filtra las horas pico por <strong>trabajador</strong> para ver cuándo
      está más ocupado cada uno, o pulsa <strong>Todos</strong> para ver el
      negocio completo.
    </>
  ),

  paso(
    '.tour-resumen-anio',
    <>
      <strong>Resumen del año</strong>. Con las flechas puedes consultar
      <strong> años anteriores</strong>.
    </>,
    'bottom'
  ),

  paso(
    '.tour-resumen-vista',
    <>
      Cambia entre <strong>ingresos y gastos</strong> o{' '}
      <strong>reservas</strong>. La línea discontinua es el año anterior, para
      comparar.
    </>,
    'bottom'
  ),

  paso(
    '.tour-resumen-meses',
    <>
      Los 12 meses de un vistazo: cuanto más intenso el color, mejor mes.{' '}
      <strong>Pulsa un mes</strong> para ver su detalle en las métricas de
      arriba.
    </>
  ),
];

export const getDashboardTourSteps = (): Step[] =>
  DashboardTourSteps.filter(
    (s) =>
      s.target === 'body' ||
      (typeof s.target === 'string' && !!document.querySelector(s.target))
  );

export default DashboardTourSteps;
